import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { playerJoinGame } from 'api/GameAPI';
import { getInitialSqPlayer, getMarker } from 'helpers/ArrayHelpers';
import { memoize } from 'proxy-memoize';
import { resetActiveGame, setActiveSqGame } from 'slices/ActiveGameSlice';
import { AppThunk, RootState } from 'slices/store';
import { FbSqUser, SqGame, SqGameBackEndModel, SqGameID, SqPlayer, SquaresProps, SqUserID } from 'types/SqGame.types';

const initialState: SquaresProps = {
  isLoading: false,
  games: [],
  resumeGames: [],
  completedGames: [],
  activeSqGameId: '',
  activeSqPlayerId: '',
};

// #region ACTIVE GAME ACTIONS ************************************************

// export const doAddPlayerToActiveGame =
//   (player: SqPlayer): AppThunk =>
//   async (dispatch, getStore) => {
//     const activeGame = getActiveSqGame(getStore());
//     if (!activeGame) {
//       console.error(`${Emojis.warning} ${player.id} No game selected`);
//       return;
//     }
// const marker = getNextUnusedMarker(activeGame.players);
// const newPlayer: SqPlayer = {
//   ...player,
//   marker,
//   numberOfPicks: 10,
//   gameId: activeGame.sqGameId,
// };
// await addPlayerToGameInFB(newPlayer);
// const playerIds = [...activeGame.playerIds, player.id];
// const openSquares = activeGame.openSquares - Math.min(10, activeGame.openSquares);
// const activeGameTemp = { ...activeGame, playerIds, openSquares };
// updateGamePlay(activeGameTemp);
// dispatch(setModal({ kind: ShowModalKind.ShowJoinGameModal, isVisible: false }));
// };

// export const doUpdateStartingActiveGameTime =
//   (time: number): AppThunk =>
//   (dispatch, getStore) => {
//     const msg = `${Emojis.eyes} %c time ${time} %c, %c date${new Date(time).toISOString()} %c`;
//     console.log(msg, ValueCls, '', ValueCls, '');
//     const ag = getStore().activegame;
//     if (ag) {
//       updateGamePlay({ ...ag, startingDate: time });
//     }
//   };
// #endregion

// #region GAME LOADERS *******************************************************
export const doCreateGame =
  (game: SqGame): AppThunk =>
  async (dispatch) => {
    dispatch(addGame(game));
    dispatch(setActiveSqGameId(game.sqGameId));
    dispatch(setActiveSqPlayerId(game.playerIds[0]));
    dispatch(setActiveSqGame(game));
  };

export const doLoadExistingGame =
  (game: SqGame): AppThunk =>
  async (dispatch, getStore) => {
    const userId: SqUserID = getStore().users.user?.uid;
    const hasUserInGame = game.playerIds.includes(userId);

    if (hasUserInGame) dispatch(setActiveSqPlayerId(userId));

    dispatch(setActiveSqGameId(game.sqGameId));
    dispatch(setActiveSqGame(game));
  };

export const updateReduxOnPlayerJoinGame =
  (game: SqGame, player: SqPlayer): AppThunk =>
  (dispatch) => {
    dispatch(setActiveSqPlayerId(player.id));
    dispatch(setActiveSqGame(game));
  };

export const doJoinExistingGame =
  (game: SqGameBackEndModel, user: FbSqUser): AppThunk =>
  async (dispatch) => {
    const player: SqPlayer = getInitialSqPlayer(user);
    player.gameId = game.sqGameId;
    player.marker = getMarker(game.playerIds.length);

    const callback = (updatedGame: SqGame, newPlayer: SqPlayer) =>
      dispatch(updateReduxOnPlayerJoinGame(updatedGame, newPlayer));

    playerJoinGame(player, callback);
  };

export const doGoToLobby =
  (toLobby: () => void | Promise<void>): AppThunk =>
  (dispatch) => {
    dispatch(setActiveSqGameId(''));
    dispatch(setActiveSqPlayerId(''));
    dispatch(resetActiveGame());
    toLobby();
  };

export const squaresSlice = createSlice({
  name: 'sq',
  initialState,
  reducers: {
    setIsLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
    // AVAILABLE GAMES ********************************************************
    setGames: (state, action: PayloadAction<SqGameBackEndModel[]>) => {
      state.games = action.payload;
    },
    addGame: (state, action: PayloadAction<SqGameBackEndModel>) => {
      state.games = [...state.games, action.payload];
    },
    setResumeGames: (state, action: PayloadAction<SqGameBackEndModel[]>) => {
      state.resumeGames = action.payload;
    },
    // COMPLETED GAMES ********************************************************
    setCompletedGames: (state, action: PayloadAction<SqGameBackEndModel[]>) => {
      state.completedGames = action.payload;
    },
    // GAME *******************************************************************
    setActiveSqGameId: (state, action: PayloadAction<SqGameID>) => {
      state.activeSqGameId = action.payload;
    },
    setActiveSqPlayerId: (state, action: PayloadAction<SqUserID>) => {
      state.activeSqPlayerId = action.payload;
    },

    // RESET ******************************************************************
    resetSquares: () => {
      return { ...initialState };
    },
  },
});

export const {
  setIsLoading,
  setGames,
  addGame,
  setResumeGames,
  setCompletedGames,
  setActiveSqGameId,
  setActiveSqPlayerId,
  resetSquares,
} = squaresSlice.actions;

export const getIsLoading = (state: RootState): boolean => state.sq.isLoading;
// #region GAMES *************************************************************
export const getAllSqGames = (state: RootState): SqGameBackEndModel[] => state.sq.games;
export const getActiveSqGameID = (state: RootState): string => state.sq.activeSqGameId;
export const getActiveSqPlayerId = (state: RootState): SqUserID => state.sq.activeSqPlayerId;
export const getCompletedGames = memoize((state: RootState): SqGameBackEndModel[] => {
  const user = state.users.user;
  return state.sq.completedGames.filter((game) => !game.bannedPlayerIds.includes(user.uid));
});
export const getResumeGames = (state: RootState): SqGameBackEndModel[] => state.sq.resumeGames;
export const getAvailableToResumeGames = memoize((state: RootState) => {
  const user = state.users.user;
  if (!user) return [];

  return state.sq.games.filter((game) => game.playerIds.includes(user.uid) && !game.bannedPlayerIds.includes(user.uid));
});

export const getAvailableToJoinGames = memoize((state: RootState) => {
  const resumeGameIds = getAvailableToResumeGames(state).map((game) => game.sqGameId);
  const gamesWithoutPlayer = state.sq.games.filter(
    (game) => !resumeGameIds.includes(game.sqGameId) && !game.bannedPlayerIds.includes(state.users.user?.uid ?? '')
  );
  return gamesWithoutPlayer.filter((g) => g.gameProgress === 0 && !g.boardLocked && g.openSquares > 0);
});
// #endregion

export default squaresSlice.reducer;
